import { styled } from '@linaria/react';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Button, IconButton, Menu, MenuItem } from '@mui/material';
import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExtendSelectionCommand } from '../../../../core/mapV2/commands/ExtendSelectionCommand';
import { RemoveFromSelectionCommand } from '../../../../core/mapV2/commands/RemoveFromSelectionCommand';
import { formatKilometers, formatMeters, formatSectionLabel } from '../../../../core/mapV2/format';
import {
    attributeBreakdownsQuery,
    extendActionsQuery,
    referenceColoringQuery,
    selectionSummaryQuery,
} from '../../../../core/mapV2/queries';
import { EXTEND_KINDS, formatAxes, MultiAttributeField } from '../../../../core/mapV2/selection';
import { editDialogOpened, listHoveredIdSet } from '../../../../core/mapV2/state';
import { selectedRoadSectionsQuery } from '../../../../core/reference/queries';
import theme from '../../../../design/theme';
import { useAppDispatch, useAppSelector } from '../../../../types';
import { Group, GroupTitle, Sheet } from './panelStyles';

// Retained sections listed before "N autres".
const COLLAPSED_LIST_SIZE = 3;
const NO_COLOR = '#9CA3AF';
// Fields edited most often get a button of their own, the others sit behind "···".
const QUICK_FIELDS: MultiAttributeField[] = ['category', 'gestionary', 'roadType'];
const MORE_FIELDS: MultiAttributeField[] = ['trucks', 'urban', 'year', 'width'];

const Title = styled.h2`
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: ${theme.color.grey.dark};
`;

const Summary = styled.p`
    margin: 4px 0 16px;
    font-size: 12px;
    color: ${theme.color.grey.light};
`;

const Table = styled.div`
    border: 1px solid ${theme.color.grey.medium};
    border-radius: 8px;
    overflow: hidden;
`;

const AttributeRow = styled.div`
    display: grid;
    grid-template-columns: 44% 56%;
    border-bottom: 1px solid ${theme.color.grey.medium};
    font-size: 12px;

    &:last-child {
        border-bottom: 0;
    }

    & > span {
        padding: 8px 10px;
    }

    & > span:first-child {
        color: ${theme.color.grey.light};
    }
`;

const Expand = styled.button`
    padding: 0;
    border: 0;
    background: transparent;
    font: inherit;
    cursor: pointer;
    color: #295eef;
`;

const Breakdown = styled.div`
    grid-column: 1 / -1;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 2px 10px 8px;
`;

const Chip = styled.span`
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 11px;
    background-color: ${theme.color.grey.medium};
`;

const Actions = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
`;

const Subtitle = styled.span`
    display: block;
    margin: 12px 0 6px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${theme.color.grey.light};
`;

const RetainedRow = styled.div`
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 7px 10px;
    border-bottom: 1px solid ${theme.color.grey.medium};
    font-size: 12px;

    &:last-child {
        border-bottom: 0;
    }

    &:hover {
        background-color: #f8fafc;
    }
`;

const Dot = styled.span`
    flex: 0 0 9px;
    width: 9px;
    height: 9px;
    border-radius: 3px;
`;

const RowLabel = styled.span`
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const RowLength = styled.span`
    color: ${theme.color.grey.light};
`;

const Remove = styled.button`
    padding: 0;
    border: 0;
    background: transparent;
    font-size: 14px;
    line-height: 1;
    cursor: pointer;
    color: ${theme.color.grey.light};

    &:hover {
        color: #ef4444;
    }
`;

const LinkButton = styled.button`
    margin-top: 7px;
    padding: 0;
    border: 0;
    background: transparent;
    font-size: 11.5px;
    cursor: pointer;
    color: #295eef;
`;

// Right-panel content when several sections are selected: count and length, the referential attributes
// (one value, or how many and which), bulk edit buttons that open the edit dialog, the
// "extend the selection" shortcuts, and the retained sections that can be removed one by one.
export const MultiSelectionPanel: FC = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const summary = useAppSelector(selectionSummaryQuery);
    const breakdowns = useAppSelector(attributeBreakdownsQuery);
    const extend = useAppSelector(extendActionsQuery);
    const sections = useAppSelector(selectedRoadSectionsQuery);
    const coloring = useAppSelector(referenceColoringQuery);
    const [expandedField, setExpandedField] = useState<MultiAttributeField | null>(null);
    const [listOpen, setListOpen] = useState(false);
    const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);

    if (summary.kind !== 'multi') {
        return null;
    }
    const shown = listOpen ? sections : sections.slice(0, COLLAPSED_LIST_SIZE);
    const hidden = sections.length - shown.length;
    const fieldLabel = (field: MultiAttributeField) => t(`mapV2.sheet.${field}`);
    const editButton = (field: MultiAttributeField) => (
        <Button
            key={field}
            size="small"
            variant="outlined"
            onClick={() => dispatch(editDialogOpened(field))}
            sx={{ textTransform: 'none', fontSize: 12 }}
        >
            {t('mapV2.multi.editField', { field: fieldLabel(field).toLowerCase() })}
        </Button>
    );

    return (
        <Sheet data-testid="map-v2-multi-selection-panel">
            <Title>{t('mapV2.multi.title', { count: summary.count })}</Title>
            <Summary>
                {t('mapV2.multi.summary', {
                    length: formatKilometers(summary.lengthMeters),
                    axes: formatAxes(summary.axes),
                })}
            </Summary>

            <Group>
                <GroupTitle>{t('mapV2.multi.attributes')}</GroupTitle>
                <Table>
                    {breakdowns.map((breakdown) => (
                        <AttributeRow key={breakdown.field} data-testid={`map-v2-multi-attribute-${breakdown.field}`}>
                            <span>{fieldLabel(breakdown.field)}</span>
                            <span>
                                {breakdown.homogeneous ? (
                                    breakdown.value
                                ) : (
                                    <Expand
                                        type="button"
                                        aria-expanded={expandedField === breakdown.field}
                                        onClick={() =>
                                            setExpandedField(expandedField === breakdown.field ? null : breakdown.field)
                                        }
                                    >
                                        {t('mapV2.multi.values', { count: breakdown.distinct })}{' '}
                                        {expandedField === breakdown.field ? '▾' : '▸'}
                                    </Expand>
                                )}
                            </span>
                            {!breakdown.homogeneous && expandedField === breakdown.field ? (
                                <Breakdown>
                                    {breakdown.values.map((entry) => (
                                        <Chip key={entry.value}>{`${entry.value} ×${entry.count}`}</Chip>
                                    ))}
                                </Breakdown>
                            ) : null}
                        </AttributeRow>
                    ))}
                </Table>
            </Group>

            <Group>
                <GroupTitle>{t('mapV2.multi.bulkEdit')}</GroupTitle>
                <Actions>
                    {QUICK_FIELDS.map(editButton)}
                    <IconButton
                        size="small"
                        aria-label={t('mapV2.multi.moreFields')}
                        title={t('mapV2.multi.moreFields')}
                        onClick={(event) => setMoreAnchor(event.currentTarget)}
                    >
                        <MoreHorizIcon fontSize="small" />
                    </IconButton>
                    <Menu anchorEl={moreAnchor} open={moreAnchor !== null} onClose={() => setMoreAnchor(null)}>
                        {MORE_FIELDS.map((field) => (
                            <MenuItem
                                key={field}
                                sx={{ fontSize: 13 }}
                                onClick={() => {
                                    setMoreAnchor(null);
                                    dispatch(editDialogOpened(field));
                                }}
                            >
                                {t('mapV2.multi.editField', { field: fieldLabel(field).toLowerCase() })}
                            </MenuItem>
                        ))}
                    </Menu>
                </Actions>

                {extend ? (
                    <>
                        <Subtitle>{t('mapV2.multi.extend')}</Subtitle>
                        <Actions>
                            {EXTEND_KINDS.map((kind) => (
                                <Button
                                    key={kind}
                                    size="small"
                                    variant="outlined"
                                    sx={{ textTransform: 'none', fontSize: 11.5 }}
                                    onClick={() => dispatch(ExtendSelectionCommand({ kind, baseId: extend.baseId }))}
                                >
                                    {t(`mapV2.extend.${kind}`, {
                                        value: extend.hints.find((h) => h.kind === 'axis')?.value,
                                    })}
                                </Button>
                            ))}
                        </Actions>
                    </>
                ) : null}
            </Group>

            <Group>
                <GroupTitle>{t('mapV2.multi.retained')}</GroupTitle>
                <Table>
                    {shown.map((section) => (
                        <RetainedRow
                            key={section.id}
                            data-testid="map-v2-retained-section"
                            onMouseEnter={() => dispatch(listHoveredIdSet(section.id))}
                            onMouseLeave={() => dispatch(listHoveredIdSet(null))}
                        >
                            <Dot style={{ backgroundColor: coloring?.colorById.get(section.id) ?? NO_COLOR }} />
                            <RowLabel>{formatSectionLabel(section)}</RowLabel>
                            <RowLength>{formatMeters(Math.abs(section.cumulEnd - section.cumulStart))}</RowLength>
                            <Remove
                                type="button"
                                aria-label={t('mapV2.multi.remove')}
                                title={t('mapV2.multi.remove')}
                                onClick={() => dispatch(RemoveFromSelectionCommand(section.id))}
                            >
                                ×
                            </Remove>
                        </RetainedRow>
                    ))}
                </Table>
                {hidden > 0 || (listOpen && sections.length > COLLAPSED_LIST_SIZE) ? (
                    <LinkButton type="button" onClick={() => setListOpen(!listOpen)}>
                        {hidden > 0 ? t('mapV2.multi.more', { count: hidden }) : t('mapV2.multi.less')}
                    </LinkButton>
                ) : null}
            </Group>
        </Sheet>
    );
};
